import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { nanoid } from "nanoid";
import { isEqual } from "lodash";
import { toast as sonnerToast } from "sonner";

import { JobsContext, SearchContext } from "context/providers";
import SavedSearchDropdown from "./SavedSearchDropdown";
import SaveSearchDialog from "./SaveSearchDialog";
import DeleteSearchDialog from "./DeleteSearchDialog";
import { aiSkillsToString } from "utils/helpers/search";
import { cn } from "utils";

const oldSearchCriteriaCookieName = "SEARCH_CRITERIA";
const userSearchCriteriaCookieName = "USER_SEARCH_CRITERIA";
const jobSearchCriteriaCookieName = "JOB_SEARCH_CRITERIA";

export function migrateSearchCriteriaCookie() {
  const oldSearchCriteria = localStorage.getItem(oldSearchCriteriaCookieName);

  if (!oldSearchCriteria) return;

  try {
    const parsedCriteriaArray = JSON.parse(oldSearchCriteria);

    if (!Array.isArray(parsedCriteriaArray)) return;

    parsedCriteriaArray.forEach((searchCriteria) => {
      const { criteria } = searchCriteria;

      if (!criteria) return;

      const isUserSearch = Object.keys(criteria.configProps || {}).length === 0;
      const targetCookieName = isUserSearch
        ? userSearchCriteriaCookieName
        : jobSearchCriteriaCookieName;

      const existingSearches = JSON.parse(
        localStorage.getItem(targetCookieName) || "[]"
      );
      const updatedSearches = [...existingSearches, searchCriteria];
      localStorage.setItem(targetCookieName, JSON.stringify(updatedSearches));
    });

    localStorage.removeItem(oldSearchCriteriaCookieName);
  } catch (error) {
    console.error("Error migrating search criteria:", error);
  }
}

export function getSavedSearchesFromStorage(searchCookieName) {
  return JSON.parse(localStorage.getItem(searchCookieName) || "[]");
}

export function showToast({ text, type }) {
  sonnerToast.custom((id) => (
    <div
      id={id}
      className={cn("mx-auto rounded-md shadow-lg font-semibold px-3 py-1", {
        "text-green-500 bg-green-50": type === "success",
        "text-slate-500 bg-slate-50": type === "delete",
      })}
    >
      {text}
    </div>
  ));
}

function SavedSearches() {
  const { searchState, setSearchState } = useContext(SearchContext);
  const { jobOpp } = useContext(JobsContext);

  const isJobPage = useMemo(() => !!jobOpp.id, [jobOpp]);
  const searchCookieName = isJobPage
    ? jobSearchCriteriaCookieName
    : userSearchCriteriaCookieName;

  // Migrate old search criteria if it exists
  useEffect(() => {
    migrateSearchCriteriaCookie();
  }, []);

  const [inputs, setInputs] = useState({ criteriaTitle: "" });
  const [errors, setErrors] = useState({});
  const [selected, setSelected] = useState(null);
  const [savedSearches, setSavedSearches] = useState(
    getSavedSearchesFromStorage(searchCookieName)
  );
  const [openSaveDialog, setOpenSaveDialog] = useState(false);
  const [searchToDelete, setSearchToDelete] = useState(null);
  const [openSelect, setOpenSelect] = useState(false);

  const allowStateChangeCheck = useRef(true);

  useEffect(() => {
    if (!selected?.value || !allowStateChangeCheck.current) return;

    allowStateChangeCheck.current = false;
    const match = savedSearches.find((e) => e.id === selected.value);

    if (!isEqual(match?.criteria, searchState)) {
      setSelected(null);
    }

    setTimeout(() => {
      allowStateChangeCheck.current = true;
    }, 1500);
  }, [searchState, savedSearches, selected]);

  const handleSave = (data) => {
    const newId = nanoid();

    let criteria = { ...searchState };

    if (!isJobPage) {
      criteria = {
        ...criteria,
        skills: {
          ...criteria.skills,
          required: searchState.basicSearch
            ? []
            : aiSkillsToString(searchState.customSearchQuery),
        },
      };
    }

    const updated = [
      ...savedSearches,
      { id: newId, title: data.criteriaTitle, criteria },
    ];

    localStorage.setItem(searchCookieName, JSON.stringify(updated));
    setSavedSearches(updated);
    setInputs({ criteriaTitle: "" });
    setSelected({ value: newId, label: data.criteriaTitle });
    setOpenSaveDialog(false);

    showToast({ text: "Search Saved!", type: "success" });
  };

  const handleDelete = (id) => {
    const updated = savedSearches.filter((s) => s.id !== id);
    localStorage.setItem(searchCookieName, JSON.stringify(updated));
    setSavedSearches(updated);
    setSelected(null);
    setSearchToDelete(null);
    setOpenSelect(false);

    showToast({ text: "Search Deleted.", type: "delete" });
  };

  const handleRestore = (id) => {
    const savedSearch = savedSearches.find((s) => s.id === id);
    if (savedSearch) setSearchState(savedSearch.criteria);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <SavedSearchDropdown
        open={openSelect}
        setOpen={setOpenSelect}
        selected={selected}
        setSelected={setSelected}
        savedSearches={savedSearches}
        onRestore={handleRestore}
        onDeleteClick={setSearchToDelete}
      />

      <SaveSearchDialog
        open={openSaveDialog}
        setOpen={setOpenSaveDialog}
        inputs={inputs}
        setInputs={setInputs}
        errors={errors}
        setErrors={setErrors}
        onSave={handleSave}
      />

      <DeleteSearchDialog
        search={searchToDelete}
        onDelete={handleDelete}
        onCancel={() => setSearchToDelete(null)}
      />
    </div>
  );
}

export default SavedSearches;
